import * as React from "react";
import { Chip, Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";

import { useMe, useOrgs, useSetActiveOrg } from "@/api/hooks";

/** Names the org the session history is filtered to, as a removable chip.
 *
 * The active-org pin (AppShell switcher / admin filter) silently
 * filters ``GET /sessions`` — and for a platform admin pinned to an
 * org they are not a member of, the backend deliberately answers with
 * an EMPTY list. Both states looked like "my old sessions are gone".
 * This chip sits in the filter row, names the scope and clears it with
 * its ×. It replaced a permanent banner above "New session" that read
 * like an unfinished task.
 */
export function SessionScopeChip(): React.JSX.Element | null {
  const { t } = useTranslation();
  const me = useMe();
  // The same list (and cache entry) as the app bar's org switcher, so the
  // chip names the org the way the switcher does.
  const orgs = useOrgs();
  const setActiveOrg = useSetActiveOrg();
  const scope = me.data?.admin_scope;
  const slug = scope?.active_org_slug ?? null;
  // Only a PERSISTED pin filters the list. The backend also fills
  // active_org_slug with a display default for every non-global admin,
  // which would make the chip permanent for every regular user — with
  // an × that could not change anything.
  if (!slug || !scope?.active_org_pinned) return null;
  const memberSlugs = new Set([
    ...(scope?.org_admin_slugs ?? []),
    ...(scope?.org_manager_slugs ?? []),
    ...(scope?.org_member_slugs ?? []),
  ]);
  // One org and no cross-org view: nothing to "show all" of.
  if (!scope?.is_global_admin && memberSlugs.size <= 1) return null;
  const member = memberSlugs.has(slug);
  const org = orgs.data?.find((o) => o.slug === slug)?.name ?? slug;
  // Not `disabled` while pending: a disabled child swallows the tooltip's
  // events. The guard keeps a double click from firing twice instead.
  const clear = () => {
    if (!setActiveOrg.isPending) setActiveOrg.mutate({ slug: null });
  };
  return (
    <Tooltip
      describeChild
      title={
        member
          ? t("session.scope_member", "Showing only sessions in {{org}}.", { org })
          : t(
              "session.scope_non_member",
              "Your view is pinned to {{org}}, which you are not a member of — your own sessions are hidden.",
              { org },
            )
      }
    >
      <Chip
        size="small"
        variant="outlined"
        // A pin to an org the user is not a member of hides their own
        // sessions: worth the warning colour the banner used to carry.
        color={member ? "primary" : "warning"}
        label={org}
        aria-label={t("session.scope_chip_aria", { org })}
        onClick={clear}
        onDelete={clear}
        sx={{ flex: "0 1 auto", minWidth: 0, maxWidth: "50%" }}
      />
    </Tooltip>
  );
}
